import { toast } from 'sonner'
import { create } from 'zustand'

import { registerSyncListener } from '@/api/sync-events'

/** 進捗表示に必要な最小限のジョブ状態 */
export type OcrJobProgress = {
  id: number
  productId: number
  status: 'pending' | 'running' | 'completed' | 'failed'
  progress: number
  processedPages: number
  totalPages: number
  message: string
}

type OcrJobStore = {
  /** product_id をキーにした進行中ジョブ */
  jobs: Map<number, OcrJobProgress>
  upsertJob: (job: OcrJobProgress) => void
  clearJob: (productId: number) => void
}

export const useOcrJobStore = create<OcrJobStore>((set) => ({
  jobs: new Map(),

  upsertJob: (job) =>
    set((state) => {
      const next = new Map(state.jobs)
      next.set(job.productId, job)
      return { jobs: next }
    }),

  clearJob: (productId) =>
    set((state) => {
      if (!state.jobs.has(productId)) return state
      const next = new Map(state.jobs)
      next.delete(productId)
      return { jobs: next }
    }),
}))

// ============================================================================
// SSE リモートイベント購読
// ============================================================================
// ワーカーはページ単位で進捗を配信する。完了・失敗はトーストで知らせ、
// 一覧に残り続けないよう少し置いてから状態を破棄する。
const CLEAR_DELAY_MS = 5_000

registerSyncListener((event) => {
  if (event.type !== 'ocr.updated') return

  const store = useOcrJobStore.getState()
  const previous = store.jobs.get(event.product_id)
  const status = (event.status ?? previous?.status ?? 'pending') as OcrJobProgress['status']

  store.upsertJob({
    id: event.id,
    productId: event.product_id,
    status,
    progress: event.progress ?? previous?.progress ?? 0,
    processedPages: event.processed_pages ?? previous?.processedPages ?? 0,
    totalPages: event.total_pages ?? previous?.totalPages ?? 0,
    message: event.message ?? previous?.message ?? '',
  })

  if (status === 'completed') {
    toast.success('再OCRが完了しました')
  } else if (status === 'failed') {
    toast.error(event.message ?? '再OCRに失敗しました')
  } else {
    return
  }

  setTimeout(() => {
    useOcrJobStore.getState().clearJob(event.product_id)
  }, CLEAR_DELAY_MS)
})
